import React, {useState, useEffect} from 'react';
import { Stack, Typography, Checkbox, FormControlLabel, FormGroup, Button, InputAdornment, Link, useTheme, Box } from '@mui/material';
import { useForm } from 'react-hook-form';

import { DateField, FormProvider, TextField } from 'components/Form';
import { formatDateTime } from 'features/qna/utils/formatDate';
import { useAuth } from 'lib/auth';
import { AdditionalPeriods } from 'components/AdditionalPeriod/AdditionalPeriod';
import { ImageAvatar } from 'components/Avatar';
import { Link as RouterLink } from 'react-router-dom';
import { paths } from 'routes';
import { DELEGON_URL } from 'configuration';
import { isoToDateString } from 'utils/dateFormatter';
import { CompanyProfile } from 'components/CompanyProfile/CompanyProfile';
import { SmallProfile } from 'components/CompanyProfile/SmallProfile';
import { RedMessageBar } from 'components/RedMessageBar';
import { LinkedinIcon, LinkedinShareButton } from 'react-share';
import { StarButton } from './StarButton';
import { ApplyForm } from './ApplyForm';
import { RepostButton } from './RepostButton';
import { getJobAdLocations } from 'utils/jobAdLocations';
import { StaticLocationMap } from 'components/JobAdsMap/StaticLocationMap';

const detailsTabName = 'details';

const getDefaultValues = () => ({
  // agreement: null,
  buyer: null,
  // dps: null,
  headline: '',
  role: '',
  experienceLevel: '',
  description: '',
  type: 'On site',
  hoursPerWeek: 40,
  maxRate: '',
  public_id: '',
  referenceNumber: '',
  startDate: null,
  endDate: null,
  applicationType: 'email',
  applicationEmail: '',
  applicationURL: '',
  lastDayToRespond: null,
  questionsEndDate: '',
  // agreementOrDpsName: '',
  buyerName: '',
  // dpsName: '',
  locations: [],
  additionalPeriods: [],
  competenceAreas: [],
  supplierID: null,
  buyerLogoURL: '',
});

export const DetailsTab = ({ currentTab, jobAd }) => {
  const theme = useTheme(); 
  const { account, user } = useAuth(); 
  const [ loading, setLoading ] = useState(true);
  const [ additionalPeriods, setAdditionalPeriods ] = useState(jobAd?.additional_periods);
  const [ initialPeriod, setInitialPeriod ] = useState([
    {
      label: "Initial",
      startDate: jobAd?.start_date,
      endDate: jobAd?.end_date,
      status: "used",
    }
  ]);

  const lastDayToRespond = new Date(jobAd.last_day_to_respond);
  lastDayToRespond.setHours(23, 59, 59, 999);
  const isExpired = lastDayToRespond.getTime() < new Date().getTime();
  const isWithdrawn = jobAd.status === 'withdrawn';
  const isJobAdActive = !isExpired && !isWithdrawn;
  const companyID = user?.Company?.company_id;
  const isOwner = !!companyID && (companyID === jobAd?.supplier_id || companyID === jobAd?.buyer_id);
  const canApplyInApp =
    jobAd?.application_method === 'in_app' &&
    jobAd?.can_offer !== false &&
    user?.Company?.Type === 'supplier' &&
    !isOwner;

  const methods = useForm({
    defaultValues: getDefaultValues(),
  });

  useEffect(() => {
    const {
      headline,
      buyer_id,
      buyer_name,
      buyer_logo_url,
      fa_id,
      fa_name,
      dps_id,
      dps_name,
      role,
      experience_level,
      description,
      type,
      hours_per_week,
      max_rate,
      reference_number,
      public_id,
      locations,
      places,
      competence_areas,
      start_date,
      end_date,
      additional_periods,
      application_method,
      application_email,
      application_url,
      last_day_to_respond,
      questions_end_date,
      supplier_id,
    } = jobAd;   
  
    methods.reset({
      // agreement: fa_id || dps_id,
      buyer: buyer_id,
      // dps: dps_id,
      headline: headline,
      role: role,
      experienceLevel: experience_level,
      description: description,
      type: type,
      hoursPerWeek: hours_per_week,
      maxRate: max_rate,
      referenceNumber: reference_number,
      public_id: public_id,
      locations: getJobAdLocations({ locations, places }),
      competenceAreas: competence_areas,
      startDate: new Date(start_date),
      endDate: new Date(end_date),
      additionalPeriods: additional_periods,
      applicationType: application_method,
      applicationEmail: application_email,
      applicationURL: application_url,
      lastDayToRespond: new Date(last_day_to_respond),
      questionsEndDate: formatDateTime(questions_end_date),
      supplierID: supplier_id,
      // agreementOrDpsName: fa_name ? `${fa_name} (Agreement)` : dps_name ? `${dps_name} (DPS)` : '',
      buyerName: buyer_name,
      // dpsName: dps_name,
      buyerLogoURL: buyer_logo_url,
    });
  
    setAdditionalPeriods(jobAd?.additional_periods);
    const tempPeriod = [...initialPeriod];
    tempPeriod[0].startDate = jobAd?.start_date;
    tempPeriod[0].endDate = jobAd?.end_date;
    setInitialPeriod(tempPeriod);
    setLoading(false);
  }, [jobAd]);

  const onSubmit = () => {};

  if (currentTab !== detailsTabName) return null;

  if (loading) {
    return null;
  }

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack direction="row" width="100%" height="100%" bgcolor={'background.paper'} gap={2}>
        <Stack
          sx={{
            flex: 2,
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Stack
            sx={{
              flex: 2,
              flexDirection: "column",
              gap: 2,
              border: "1px solid",
              borderRadius: 1,
              borderColor: "border.jobAd",
              padding: 2,
            }}
          >
            <Stack
              direction={"row"}
              gap={2}
              alignItems={"center"}
            >
              {jobAd?.buyer_hidden ? (
                <ImageAvatar anonymous name={jobAd?.buyer_name || ''} />
              ) : (
                <Link
                  component={RouterLink}
                  to={paths.public.companyPublicProfile("buyer", jobAd?.buyer_slug)}
                  color="inherit"
                  underline="none"
                >
                  <ImageAvatar img={jobAd?.buyer_logo_url || ''} name={jobAd?.buyer_name || ''} />
                </Link>
              )}
              <Typography variant="h5" fontWeight={600} fontSize={'1.5rem'} lineHeight={"32px"} color='text.black' flex={1}>{jobAd?.headline}</Typography>
              <StarButton jobAdID={jobAd?.ad_id} isStarred={jobAd?.is_starred} assignedUserID={jobAd?.assigned_user_id} size={24} />
            </Stack>
            {!isJobAdActive && (
            <Link component={RouterLink} to={paths.homepage} color="inherit" underline="none" sx={{ cursor: 'pointer', display: 'block' }}>
              <RedMessageBar
                message={
                  (isWithdrawn
                    ? "This job ad has been withdrawn and is no longer accepting new applications. "
                    : "This job ad has expired and is no longer accepting new applications. ") + "Click here to see other job ads"
                }
              />
            </Link>
            )}
            <TextField
              name="description"
              disabled
              type="text"
              fullWidth
              sx={{
                '& .MuiInputBase-root, .MuiInputBase-input': {
                  p: 0,
                  border: 'none!important',
                  backgroundColor: "background.paper",
                  py: "0px!important",
                  px: "0px!important",
                },
              }}
              multiline
              minRows={2}
              wrapperSx={{
                gap: 0,
              }}
            />
          </Stack>
          {!jobAd?.buyer_hidden && (
            <CompanyProfile company_name={jobAd?.buyer_name} square_logo_url={jobAd?.buyer_logo_url} banner_logo_url={jobAd?.buyer_banner_url} description={jobAd?.buyer_description} slug={jobAd?.buyer_slug} company_id={jobAd?.buyer_id} />
          )}
        </Stack>
        <Stack
          sx={{
            flex: 1,
            flexDirection: "column",
            gap: 4.375,
          }}
        >
          <Stack
            sx={{
              flexDirection: "column",
              gap: 2.5,
              border: "1px solid",
              borderRadius: 1,
              borderColor: "border.jobAd",
              padding: 2,
              backgroundColor: "background.jobAd",
              alignSelf: "stretch"
            }}
          >
            {jobAd.can_offer !== false && (
              <>
                {jobAd.application_method === 'email' && jobAd?.application_email?.length > 0 && (
                  <Button
                    size="medium"
                    variant="contained"
                    color="primary"
                    sx={{ mb: 0.5, width: 1}}
                    href={`mailto:${jobAd?.application_email}`}
                    target={"_top"}
                    rel={"noopener noreferrer"}
                    disabled={!isJobAdActive}
                  >
                    Apply by email
                  </Button>
                )}
                {jobAd.application_method === 'url' && jobAd?.application_url?.length > 0 && (
                  <Button
                    size="medium"
                    variant="contained"
                    color="primary"
                    sx={{ mb: 0.5, width: 1}}
                    href={jobAd?.application_url}
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                    disabled={!isJobAdActive}
                  >
                    Apply by URL
                  </Button>
                )}
                {canApplyInApp && (
                  <ApplyForm jobAd={jobAd} disabled={!isJobAdActive} buttonSx={{ mb: 0.5, width: 1 }} />
                )}
                <RepostButton jobAd={jobAd} disabled={!isJobAdActive || jobAd.status !== 'active'} />
                <Typography
                  sx={{
                    borderTop: "1px solid",
                    borderColor: "#E4E5E8",
                  }}
                ></Typography>
              </>
            )}
            <Typography
              variant='body3'
              sx={{
                color: "text.black",
                fontFeatureSettings: `'liga' off, 'clig' off`,
              }}
            >
              All informations \ Details
            </Typography>
            <TextField
              name="referenceNumber"
              label="Reference Number"
              type="text"
              wrapperSx={{
                '& .MuiInputBase-root-MuiOutlinedInput-root.Mui-disabled, .Mui-disabled': {
                  backgroundColor: 'background.jobAd',
                  border: 'none',
                  pl: 0,
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  px: '0!important',
                  py: '4px!important',
                  border: "none",
                }
              }}
              disabled
            />
            <TextField
              name="role"
              label="Role"
              type="text"
              wrapperSx={{
                '& .MuiInputBase-root-MuiOutlinedInput-root.Mui-disabled, .Mui-disabled': {
                  backgroundColor: 'background.jobAd',
                  border: 'none!important',
                  pl: 0,
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  px: '0!important',
                  py: '4px!important',
                  border: "none!important",
                },
              }}
              disabled
            />
            <TextField
              name="experienceLevel"
              label="Experience Level"
              type="text"
              wrapperSx={{
                '& .MuiInputBase-root-MuiOutlinedInput-root.Mui-disabled, .Mui-disabled': {
                  backgroundColor: 'background.jobAd',
                  border: 'none',
                  pl: 0,
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  px: '0!important',
                  py: '4px!important',
                  border: "none",
                }
              }}
              disabled
            />
            <DateField
              name="lastDayToRespond"
              label="Application Deadline"
              type="text"
              wrapperSx={{
                '& .MuiInputBase-root-MuiOutlinedInput-root.Mui-disabled, .Mui-disabled, .MuiFormControl-root-MuiTextField-root': {
                  backgroundColor: 'background.jobAd',
                  border: 'none!important',
                  px: 0,
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  px: '0!important',
                  py: '4px!important',
                  border: "none!important",
                },
              }}
              disabled
            />
            <TextField
              name="questionsEndDate"
              label="End date for questions"
              type="text"
              wrapperSx={{
                '& .MuiInputBase-root-MuiOutlinedInput-root.Mui-disabled, .Mui-disabled': {
                  backgroundColor: 'background.jobAd',
                  border: 'none!important',
                  pl: 0,
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  px: '0!important',
                  py: '4px!important',
                  border: "none",
                }
              }}
              disabled
            />
            <TextField
              name="buyerName"
              label="Company"
              type="text"
              wrapperSx={{
                '& .MuiInputBase-root-MuiOutlinedInput-root.Mui-disabled, .Mui-disabled': {
                  backgroundColor: 'background.jobAd',
                  border: 'none!important',
                  pl: 0,
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  px: '0!important',
                  py: '4px!important',
                  border: "none",
                }
              }}
              disabled
            />
            <TextField
              name="maxRate"
              label="Max Rate"
              disabled
              type="text"
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                  >
                    SEK/h
                  </InputAdornment>
                )
              }}
              wrapperSx = {({palette}) => ({
                '& .MuiInputBase-root-MuiOutlinedInput-root.Mui-disabled, .Mui-disabled': {
                  backgroundColor: `${palette.background.jobAd}!important`,
                  border: 'none!important',
                  pl: 0,
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  px: '0!important',
                  py: '4px!important',
                },
                width: 150,
              })}
            />
            <TextField
              name="public_id"
              label="Unique ID"
              type="text"
              wrapperSx={{
                '& .MuiInputBase-root-MuiOutlinedInput-root.Mui-disabled, .Mui-disabled': {
                  backgroundColor: 'background.jobAd',
                  border: 'none',
                  pl: 0,
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  px: '0!important',
                  py: '4px!important',
                  border: "none",
                }
              }}
              disabled
            />
            <TextField
              name="type"
              label="Type"
              type="text"
              wrapperSx={{
                '& .MuiInputBase-root-MuiOutlinedInput-root.Mui-disabled, .Mui-disabled': {
                  backgroundColor: 'background.jobAd',
                  border: 'none',
                  pl: 0,
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  px: '0!important',
                  py: '4px!important',
                  border: "none",
                }
              }}
              disabled
            />
            <TextField
              name="hoursPerWeek"
              label="Hours per week"
              type="text"
              wrapperSx={{
                '& .MuiInputBase-root-MuiOutlinedInput-root.Mui-disabled, .Mui-disabled': {
                  backgroundColor: 'background.jobAd',
                  border: 'none',
                  pl: 0,
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  px: '0!important',
                  py: '4px!important',
                  border: "none",
                }
              }}
              disabled
            />
            {methods.getValues("locations") && methods.getValues("locations").length > 0 && (
              <TextField
                name="locations"
                label="Locations"
                disabled
                type="text"
                fullWidth
                isChip
                locations
                chipSx={{
                  backgroundColor: 'background.jobAd',
                  '& .MuiChip-label': {
                    px: 0,
                  },
                  height: 24
                }}
                parentSx={{
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 0,
                }}
              />
            )}
            <StaticLocationMap url={jobAd?.static_map_url} />
            {methods.getValues("competenceAreas") && methods.getValues("competenceAreas").length > 0 && (
              <TextField
                name="competenceAreas"
                label="Competence Areas"
                disabled
                type="text"
                fullWidth
                isChip
                chipSx={{
                  backgroundColor: 'background.jobAd',
                  '& .MuiChip-label': {
                    pl: 0,
                  },
                }}
              />
            )}
            <AdditionalPeriods
              initialPeriod={initialPeriod}
              additionalPeriods={additionalPeriods}
              handleAdditionalPeriods={null}
              readOnly
              tender
              showAsList
            />
          </Stack>
          <SmallProfile company_name={jobAd?.supplier_name} square_logo_url={jobAd?.supplier_logo_url} banner_logo_url={jobAd?.supplier_banner_url} description={jobAd?.supplier_description} slug={jobAd?.supplier_slug} company_id={jobAd?.supplier_id} company_type='supplier' />
          <Stack
            sx={{
              backgroundColor: "background.jobAd",
              border: "1px solid",
              borderColor: "border.jobAd",
              borderRadius: 1,
              p: 2,
              alignItems: "center",
              gap: 1.5
            }}
          >
            <Typography
              variant='h5'
              sx={{
                color: "grey.900"
              }}
            >
              Share
            </Typography>
            <LinkedinShareButton url={`${DELEGON_URL}${paths.public.jobAdsDetailsView(jobAd?.slug)}`}>
              <LinkedinIcon size={26} borderRadius={16} bgStyle={{fill: `${theme.palette.background.primary}`}}/>
            </LinkedinShareButton>
          </Stack>
        </Stack>
      </Stack>
    </FormProvider>
  );
};
